import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { IntroScreen } from './components/IntroScreen';
import { QuestionScreen } from './components/QuestionScreen';
import { RevealScreen } from './components/RevealScreen';
import { ResultScreen } from './components/ResultScreen';
import { QUESTIONS, tallyScores, determineArchetype } from './data/quizData';
import { ContentProvider, useArchetypes } from './context/ContentContext';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import RequireAuth from './admin/RequireAuth';
import StandaloneSharePage from './components/StandaloneSharePage';
import ResourcePage from './components/ResourcePage';
import AdminContent from './admin/AdminContent';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PHASES = { INTRO: 'intro', QUESTION: 'question', REVEAL: 'reveal', RESULT: 'result' };

function QuizApp() {
  const [phase, setPhase] = useState(PHASES.INTRO);
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [firstName, setFirstName] = useState('');
  const [introHook, setIntroHook] = useState(null); // null = use default
  const containerRef = useRef(null);
  const archetypesMap = useArchetypes();

  const { archetype, scores } = useMemo(() => {
    const totals = tallyScores(answers);
    const key = determineArchetype(totals);
    return { archetype: archetypesMap[key], scores: totals };
  }, [answers, archetypesMap]);

  // Fetch feature flag for intro hook copy (A/B variant)
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/config`, { timeout: 6000 })
      .then(({ data }) => {
        if (!cancelled && data?.intro_hook) setIntroHook(data.intro_hook);
      })
      .catch(() => { /* fall back to default */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [phase, qIndex]);

  // iframe height postMessage
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const post = () => {
      try {
        const h = Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);
        window.parent?.postMessage({ type: 'birther-quiz:height', height: h }, '*');
      } catch (e) { /* noop */ }
    };
    post();
    const ro = new ResizeObserver(post);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener('load', post);
    const interval = setInterval(post, 800);
    return () => {
      ro.disconnect();
      window.removeEventListener('load', post);
      clearInterval(interval);
    };
  }, []);

  const handleStart = () => {
    setQIndex(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setPhase(PHASES.QUESTION);
  };
  const handleSelect = (i) => setAnswers((p) => { const n = p.slice(); n[qIndex] = i; return n; });
  const handleNext = () => {
    if (answers[qIndex] == null) return;
    if (qIndex < QUESTIONS.length - 1) setQIndex((i) => i + 1);
    else setPhase(PHASES.REVEAL);
  };
  const handleBack = () => { if (qIndex > 0) setQIndex((i) => i - 1); };
  const handleUnlock = (name) => { setFirstName(name); setPhase(PHASES.RESULT); };
  const handleRetake = () => {
    setQIndex(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setFirstName('');
    setPhase(PHASES.INTRO);
  };

  return (
    <div className="App bg-linen-grain min-h-screen" ref={containerRef} data-testid="app-root">
      {phase === PHASES.INTRO && <IntroScreen onStart={handleStart} introHook={introHook} />}
      {phase === PHASES.QUESTION && (
        <QuestionScreen
          index={qIndex}
          selected={answers[qIndex]}
          onSelect={handleSelect}
          onBack={handleBack}
          onNext={handleNext}
        />
      )}
      {phase === PHASES.REVEAL && (
        <RevealScreen archetype={archetype} scores={scores} answers={answers} onUnlock={handleUnlock} />
      )}
      {phase === PHASES.RESULT && (
        <ResultScreen archetype={archetype} firstName={firstName} onRetake={handleRetake} />
      )}

      <footer className="text-center pb-10 pt-4 px-5" data-testid="app-footer">
        <p className="font-heading italic text-[16px] sm:text-[18px] leading-[1.5]" style={{ color: '#3D3830' }}>
          Made for you by Kara Mears, Midwife (CNM) &amp; Mum
        </p>
        <a
          href="https://www.karamears.com"
          target="_top"
          rel="noopener noreferrer"
          data-testid="app-footer-link"
          className="font-body text-[11px] inline-block mt-3 hover:text-[#3D3830] transition-colors"
          style={{ color: '#B8A78F', textDecoration: 'none', letterSpacing: '0.32em', textTransform: 'uppercase' }}
        >
          karamears.com
        </a>
      </footer>
    </div>
  );
}

function AdminLayout({ children }) {
  return <div className="App bg-linen-grain min-h-screen">{children}</div>;
}

function App() {
  return (
    <ContentProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<QuizApp />} />
          <Route path="/share/:archetypeKey" element={<AdminLayout><StandaloneSharePage /></AdminLayout>} />
          <Route path="/resource/:archetypeKey" element={<AdminLayout><ResourcePage /></AdminLayout>} />
          <Route path="/admin/login" element={<AdminLayout><AdminLogin /></AdminLayout>} />
          <Route
            path="/admin"
            element={
              <AdminLayout>
                <RequireAuth>
                  <AdminDashboard />
                </RequireAuth>
              </AdminLayout>
            }
          />
          <Route
            path="/admin/content"
            element={
              <AdminLayout>
                <RequireAuth>
                  <AdminContent />
                </RequireAuth>
              </AdminLayout>
            }
          />
          <Route path="*" element={<QuizApp />} />
        </Routes>
      </BrowserRouter>
    </ContentProvider>
  );
}

export default App;
